import { useEffect, useRef, useState } from 'react'
import { Stack, TextField } from '@mui/material'
import { SchemaOutlined } from '@mui/icons-material'
import { apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import AlertUI from '../../generic/AlertUI'

/** The limit the editor already applies to a workflow code; the name follows it (story 199, RN6/RN8). */
const MAX_LENGTH = 255
const limited = (value) => value.slice(0, MAX_LENGTH)

/**
 * Story 199 (CU2) - asks for the code and the name of the brand-new template a workflow is saved as, and saves it.
 * The success is announced by the host window, in its own alert, through `onSaved`; the errors are shown inside the
 * dialog, which stays open with what the user wrote so another code can be tried (POL3). One click, one request (POL4).
 * Both follow the alert convention of the workflow windows: the text goes as the alert title, with no message.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is shown
 * @param {number} props.workflowId - Id of the workflow VERSION to save as a template
 * @param {string} props.defaultCode - Proposed template code: the code of that workflow
 * @param {?string} props.defaultName - Proposed template name: the name of that workflow, if any
 * @param {Function} props.onClose - Called on cancel and once the template has been created
 * @param {Function} props.onSaved - Called once the template has been created, for the host to confirm it
 */
const SaveAsTemplateDialog = ({ open, workflowId, defaultCode, defaultName, onClose, onSaved }) => {
  const { t_ } = useSmartTranslation()
  const [errorAlert, showError, dismissError] = AlertUI()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [sending, setSending] = useState(false)
  const inFlight = useRef(false)

  useEffect(() => {
    if (open) {
      setCode(limited(defaultCode ?? ''))
      setName(limited(defaultName ?? ''))
      dismissError()
    }
  }, [open, defaultCode, defaultName, dismissError])

  const save = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setSending(true)
    try {
      await apiPost('workflows/' + workflowId + '/saveAsTemplate', { templateCode: code, templateName: name })
      onClose()
      onSaved()
    } catch (error) {
      showError({ severity: error?.name || 'error', title: t_(error?.message), message: '' })
    } finally {
      inFlight.current = false
      setSending(false)
    }
  }

  return (
    <SmartDialog
      setOpen={open}
      title={createDialogTitle({ title: t_('New template'), icon: SchemaOutlined })}
      disableAccept={!code.trim() || sending}
      acceptCallback={save}
      cancelCallback={onClose}
      renderComponent={
        <Stack gap={2} sx={{ paddingTop: 1 }}>
          {errorAlert}
          <TextField
            required
            label={t_('Template code')}
            value={code}
            onChange={(event) => setCode(limited(event.target.value))}
            inputProps={{ maxLength: MAX_LENGTH }}
          />
          <TextField
            label={t_('Template name')}
            value={name}
            onChange={(event) => setName(limited(event.target.value))}
            inputProps={{ maxLength: MAX_LENGTH }}
          />
        </Stack>
      }
    />
  )
}

/**
 * Mounts nothing until the dialog is first opened, and stays mounted afterwards so it can play its closing transition.
 * Every host (workflow table, editor header, Balanced header) renders it permanently: mounting it eagerly would
 * drag AlertUI, and with it the router context, into every one of them even when nobody asks to save a template.
 *
 * @param {Object} props - see SaveAsTemplateDialog
 */
const WorkflowSaveAsTemplateDialog = (props) => {
  const [opened, setOpened] = useState(props.open)
  useEffect(() => {
    if (props.open) setOpened(true)
  }, [props.open])
  return opened || props.open ? <SaveAsTemplateDialog {...props} /> : null
}

export default WorkflowSaveAsTemplateDialog
